import { useCallback, useMemo } from "react";
import { RootState, useAppDispatch, useAppSelector } from "@store/store";
import {
  planItemComparer,
  selectHasPromptedAddDates,
  selectIsCollectionInPlan,
  selectPlanItemByEntity,
  selectPlanItems,
  setHasPromptedAddDates,
} from "@store/plan/planReducer";
import {
  useAddPlanItemMutation,
  useRemovePlanItemMutation,
  useRemoveTourMutation,
  useAddTourMutation,
  getPlan,
} from "@store/services/plan";
import { createSelector } from "@reduxjs/toolkit";
import {
  selectClient,
  selectIsKioskEnabled,
} from "@store/client/clientReducer";
import { selectUser, setIsAuthViewOpenAction } from "@store/sessionReducer";
import { PlanItem } from "@dto/PlanItem";
import { useCreatePlanMutation } from "@store/services/plan";
import { selectPlan } from "@store/plan/planReducer";
import { createAnalyticsEvent } from "@lib/analytics";
import { eventNames } from "@lib/analyticsEventNames";
import { setOverlayScreenAction } from "@store/app/appReducer";

// TODO: There probably should a single global place to put this.
const TOUR_TYPES = ["Tour", "Challenge", "SelfGuided"];

/**
 * Prompt the user to register if they are a guest user and add the configured number of items to
 * their plan. The user should be asked each new visit.
 */
const selectPromptAuthOnAdd = createSelector(
  [
    selectClient,
    selectUser,
    selectIsKioskEnabled,
    (state: RootState) => state.plan,
  ],
  (client, user, isKioskEnabled, plan) => {
    const numberToPrompt =
      client.userAuthenticationFeature?.numberOfPlanAdditionsToPromptSignup ??
      -1;

    return (
      !user &&
      plan.numberOfPlanItemsAdded + 1 === numberToPrompt &&
      !isKioskEnabled
    );
  },
);

const selectIsAdded = (
  entityId: number,
  entityType: "Tour" | "Place" | "Event" | "Challenge" | "SelfGuided" | "Goal",
  planItems: PlanItem[],
) => {
  if (!TOUR_TYPES.includes(entityType)) {
    return (
      planItems.findIndex((x) => {
        return x.plannableId === entityId && x.plannableType === entityType;
      }) > -1
    );
  }
};

const calculatePosition = (planItems: PlanItem[], dayIndex: number | null) => {
  const sortedPlanItems = [...planItems].sort(planItemComparer);
  for (let i = 0; i < sortedPlanItems.length; ++i) {
    const current = sortedPlanItems[i];
    const next = sortedPlanItems[i + 1];
    if (
      !next ||
      (current.dayIndex === dayIndex && next.dayIndex !== dayIndex) ||
      (dayIndex !== null && next.dayIndex !== null && next.dayIndex > dayIndex)
    ) {
      return current.position + 1;
    }
  }

  return 1;
};

// Copied from App code, multi day planning portions are commented out until we are ready
const useAddRemoveToPlan = (
  id: number,
  type: "Event" | "Place" | "Goal" | "Tour" | "Challenge" | "SelfGuided",
): [
  (dayIndex?: number | null) => void,
  { isLoading: boolean; isAdded: boolean },
] => {
  const dispatch = useAppDispatch();
  const [addPlanItemApi, { isLoading: isAddingPlanItem }] =
    useAddPlanItemMutation();
  const [removePlanItemApi, { isLoading: isRemovingPlanItem }] =
    useRemovePlanItemMutation();
  const [removeTourApi, { isLoading: isRemovingTour }] =
    useRemoveTourMutation();
  const [addTourApi, { isLoading: isAddingTour }] = useAddTourMutation();
  const plan = useAppSelector(selectPlan);
  const planItem = useAppSelector(selectPlanItemByEntity(id, type));
  const planItems = useAppSelector(selectPlanItems);
  const hasDaysOrDates = !!plan?.dayDuration;
  const hasPromptedAddDates = useAppSelector(selectHasPromptedAddDates);
  const isCollectionInPlan = useAppSelector(selectIsCollectionInPlan(id, type));
  const [createPlan, { isLoading: _isCreatingPlan }] = useCreatePlanMutation();
  const planId = plan?.id;

  const isAdded = useMemo(() => {
    const result = selectIsAdded(id, type, planItems) || isCollectionInPlan;
    return result;
  }, [id, planItems, type, isCollectionInPlan]);
  const shouldPromptAuthOnAdd = useAppSelector(selectPromptAuthOnAdd);

  const addToPlan = useCallback(
    async (dayIndex: number | null) => {
      let addPlanId = planId;
      if (!plan) {
        const planCreate = await createPlan({});
        if (planCreate.data?.id) {
          // Must get plan so invalidating rtk query tags re-fetches plan.
          await dispatch(getPlan.initiate(planCreate.data?.id));
          addPlanId = planCreate.data?.id;
        }
      }

      if (!addPlanId) {
        return;
      }

      if (!TOUR_TYPES.includes(type)) {
        addPlanItemApi({
          planItem: {
            dayIndex: dayIndex,
            plannableId: id,
            plannableType: type,
            position: calculatePosition(planItems, dayIndex),
          },
          planId: addPlanId,
        });
      } else {
        addTourApi({
          planId: addPlanId,
          tourId: id,
          dayIndex: dayIndex,
        });
      }

      createAnalyticsEvent(eventNames.addPlanItem, {
        id: id,
        type: type,
      });

      if (shouldPromptAuthOnAdd) {
        dispatch(setIsAuthViewOpenAction({ isAuthViewOpen: true }));
      }
    },
    [
      planId,
      plan,
      type,
      id,
      shouldPromptAuthOnAdd,
      createPlan,
      addPlanItemApi,
      planItems,
      addTourApi,
      dispatch,
    ],
  );

  const handleAddToPlan = useCallback(
    (dayIndex?: number | null) => {
      if (!hasDaysOrDates && !hasPromptedAddDates) {
        // User has not added days/dates and we have not yet prompted.
        dispatch(setHasPromptedAddDates(true));
        dispatch(
          setOverlayScreenAction({
            name: "PlanDatePicker",
            params: {
              entityId: id,
              entityType: type,
            },
          }),
        );
      } else if (hasDaysOrDates && dayIndex === undefined) {
        // User clicked Add to Plan and has day/dates, and we need to display day/date picker.
        dispatch(
          setOverlayScreenAction({
            name: "PlanAddToDatePicker",
            params: {
              entityId: id,
              entityType: type,
            },
          }),
        );
      } else {
        // Either the user has no day/dates or dayIndex was just selected.
        addToPlan(dayIndex ?? null);
      }
    },
    [addToPlan, dispatch, hasDaysOrDates, hasPromptedAddDates, id, type],
  );

  const handleRemoveFromPlan = useCallback(() => {
    if (!planId) {
      // planId should be defined if there are items in remove state
      return;
    }

    if (planItem) {
      removePlanItemApi({
        planItemId: planItem.id,
        planId: planId,
      });
    } else if (TOUR_TYPES.includes(type)) {
      removeTourApi({
        planId: planId,
        tourId: id,
      });
    }
  }, [id, planId, planItem, removePlanItemApi, removeTourApi, type]);

  const onAddOrRemovePlanItem = useCallback(
    (dayIndex?: number | null) => {
      if (isAdded) {
        handleRemoveFromPlan();
      } else {
        handleAddToPlan(dayIndex);
      }
    },
    [handleAddToPlan, handleRemoveFromPlan, isAdded],
  );

  return [
    onAddOrRemovePlanItem,
    {
      isLoading:
        isAddingPlanItem ||
        isRemovingPlanItem ||
        isRemovingTour ||
        isAddingTour,
      isAdded: isAdded,
    },
  ];
};

export { useAddRemoveToPlan };
